import React, { Fragment, useState } from 'react';
import * as Types from 'pages/General/typings';
import _get from 'lodash/get';

// Components
import AppBar from '@material-ui/core/AppBar';
import Toolbar from '@material-ui/core/Toolbar';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardContent from '@material-ui/core/CardContent';
import Divider from '@material-ui/core/Divider';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import { ThemeProvider } from '@material-ui/styles';
import StyleModeSelector from './components/StyleModeSelector';
import ColorRow from './components/ColorRow';
import ThemingSettings from './components/ThemingSettings';
import ImageField from './components/ImageField';
import PreviewDialog from './components/PreviewDialog';

// Style
import makeStyles from '@material-ui/core/styles/makeStyles';
import createTheme from '@sift/skipper/styles/createTheme';

interface Props {
  onChange: (field: string, value: any) => void;
  values: Types.GeneralValues;
}

function Theming({ onChange, values }: Props) {
  const classes = useStyles();
  const [previewOpen, setPreviewOpen] = useState(false);

  const handlePreviewClick = () => setPreviewOpen(true);
  const handlePreviewClose = () => setPreviewOpen(false);

  return (
    <Fragment>
      <div className={classes.themingContent}>
        <Card className={classes.custom}>
          <CardHeader
            title="Style"
            classes={{
              subheader: classes.subheader,
              title: classes.title,
            }}
            subheader={
              <Fragment>
                Choose the default style of Sift or customize its look and feel by adding your logo,
                colors, etc.
              </Fragment>
            }
          />
          <StyleModeSelector
            className={classes.styleModeSelector}
            onChange={value => onChange('theme.mode', value)}
            value={values.theme.mode}
          />
          {values.theme.mode === 'custom' && (
            <Fragment>
              <Divider className={classes.divider} />
              <CardHeader
                classes={{
                  subheader: classes.subheader,
                  title: classes.title,
                }}
                title="Settings"
              />
              <CardContent className={classes.colorRows}>
                <ThemingSettings
                  title="Email Theming"
                  value={_get(values, 'theme.settings.enableEmailTheming', false)}
                  onChange={e => {
                    onChange('theme.settings.enableEmailTheming', e.target.checked);
                  }}
                  description={
                    <Fragment>
                      Turn this setting on if you would like Sift email communication to be themed
                      with your logo and colors. This will only apply to invite and emails sent
                      after a user has logged in.
                    </Fragment>
                  }
                />
              </CardContent>
              <Divider className={classes.divider} />
              {/* ------------------------- COLOR PICKERS ---------------------------- */}
              <CardHeader
                classes={{
                  subheader: classes.subheader,
                  title: classes.title,
                }}
                title="Color"
                subheader="Select colors from your color scheme."
              />
              <CardContent className={classes.colorRows}>
                <ColorRow
                  title="Primary color"
                  value={_get(values, 'theme.settings.palette.primary.main') || ''}
                  onChange={value => {
                    onChange('theme.settings.palette.primary.main', value);
                  }}
                  description={
                    <Fragment>
                      Used in the main body of homepage and the top area of other pages. Input the
                      6-digit hex color values. A dark color is highly recommended.
                    </Fragment>
                  }
                />
                <ColorRow
                  title="Secondary color"
                  value={_get(values, 'theme.settings.palette.secondary.main') || ''}
                  onChange={value => {
                    onChange('theme.settings.palette.secondary.main', value);
                  }}
                  description={
                    <Fragment>
                      Used for functional text and icons, like buttons. Input the 6-digit hex color
                      value. A dark color is highly recommended.
                    </Fragment>
                  }
                />
                <ColorRow
                  title="Highlight color"
                  value={_get(values, 'theme.settings.palette.matched.main') || ''}
                  onChange={value => {
                    onChange('theme.settings.palette.matched.main', value);
                  }}
                  description={
                    <Fragment>
                      Used to highlight results in Sift. Input the 6-digit hex color value. Pick a
                      color that will work on top of your primary color and white.
                    </Fragment>
                  }
                />
              </CardContent>
              <Divider className={classes.divider} />

              {/* --------------------------- LOGO PICKER ---------------------------- */}
              <CardHeader
                classes={{
                  subheader: classes.subheader,
                  title: classes.title,
                }}
                title="Logo"
                subheader={
                  <Fragment>
                    Upload a logo to replace the Sift logo. Displays on the top left corner across
                    pages.
                  </Fragment>
                }
              />
              <ImageField
                onChangeImageUrl={value => {
                  onChange('theme.logoUrl', value);
                }}
                onRemove={() => {
                  onChange('theme.logoUrl', undefined);
                }}
                imageSrc={values.theme.logoUrl}
                maxSize={2 * 1024 ** 2}
                description={
                  <Fragment>
                    Recommended size: ~256px. We recommend using a logo with a transparent
                    background (<code>.png</code>). The image should look good against your primary
                    color.
                  </Fragment>
                }
                example={
                  <ThemeProvider theme={createTheme(values.theme.settings)}>
                    <AppBar className={classes.appBar} position="static" color="primary">
                      <Toolbar className={classes.toolbar}>
                        <img className={classes.logo} src={values.theme.logoUrl} alt="logo" />
                      </Toolbar>
                    </AppBar>
                  </ThemeProvider>
                }
              />
              <Divider className={classes.divider} />

              {/* ----------------------- PROFILE HEADER PICKER ---------------------- */}
              <CardHeader
                title="Profile Header Image"
                classes={{
                  subheader: classes.subheader,
                  title: classes.title,
                }}
                subheader={
                  <Fragment>
                    Upload an image to replace the default background photo on the top of a person's
                    profile.
                  </Fragment>
                }
              />
              <ImageField
                onChangeImageUrl={value => {
                  onChange('theme.backgroundUrl', value);
                }}
                onRemove={() => {
                  onChange('theme.backgroundUrl', undefined);
                }}
                imageSrc={values.theme.backgroundUrl}
                maxSize={3 * 1024 ** 2}
                description={
                  <Fragment>
                    Recommended size: 2100x900 px or an image with a 21:9 aspect ratio.
                    <br />
                    The top and bottom of your image may be cut off on certain screen sizes, we
                    recommended placing important content vertically centered.
                  </Fragment>
                }
                example={
                  <div
                    className={classes.background}
                    style={{
                      background: `url(${values.theme.backgroundUrl}) center center / cover`,
                    }}
                    aria-label="Background"
                  />
                }
              />
              <Divider className={classes.divider} />

              {/* ---------------------- PROFILE PICTURE PICKER ---------------------- */}
              <CardHeader
                classes={{
                  subheader: classes.subheader,
                  title: classes.title,
                }}
                title="Profile Picture Image"
                subheader={
                  <Fragment>
                    Upload an image to replace the default profile picture for new users.
                  </Fragment>
                }
              />
              <ImageField
                onChangeImageUrl={value => {
                  onChange('theme.pictureUrl', value);
                }}
                onRemove={() => {
                  onChange('theme.pictureUrl', undefined);
                }}
                imageSrc={values.theme.pictureUrl}
                maxSize={2 * 1024 ** 2}
                description={<Fragment>Recommended size: 1:1 square image.</Fragment>}
                example={
                  <Avatar
                    className={classes.avatar}
                    alt="Profile Picture"
                    src={values.theme.pictureUrl}
                  />
                }
              />
            </Fragment>
          )}
        </Card>
        {values.theme.mode === 'custom' && (
          <Card className={classes.preview}>
            <CardHeader
              classes={{
                title: classes.title,
              }}
              title="Preview"
              subheader="Preview will be shown here once colors have been selected."
            />
            <CardContent>
              <Button onClick={handlePreviewClick} variant="contained" color="secondary">
                Preview Theme
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
      <PreviewDialog
        open={previewOpen}
        onClose={handlePreviewClose}
        settings={values.theme.settings}
        logoUrl={values.theme.logoUrl}
        backgroundUrl={values.theme.backgroundUrl}
        pictureUrl={values.theme.pictureUrl}
      />
    </Fragment>
  );
}

const useStyles = makeStyles((theme: any) => {
  const height = 64; // the height of the connect app bar

  return {
    root: {
      flex: '1 1 auto',
      display: 'flex',
      flexDirection: 'column',
    },
    layoutRoot: {
      flex: '1 1 auto',
      overflowY: 'auto',
    },
    pageContentRoot: {
      flex: '1 1 auto',
      overflowY: 'auto',
    },
    styleModeSelector: {
      margin: theme.spacing(0, 2),
      marginBottom: theme.spacing(1),
    },
    title: {
      fontWeight: theme.typography.fontWeight.semiBold,
    },
    subheader: {
      ...theme.typography.body2,
      color: theme.palette.text.primary,
    },
    colorRows: {
      '& > *:not(:last-child)': {
        marginBottom: theme.spacing(4),
      },
    },
    divider: {
      margin: theme.spacing(0, 2),
    },
    themingContent: {
      display: 'flex',
      width: '100%',
      maxWidth: 950,
      alignItems: 'flex-start',
      '& > *:not(:last-child)': {
        marginRight: theme.spacing(2),
      },
      [theme.breakpoints.down('md')]: {
        flexDirection: 'column',
      },
    },
    custom: {
      maxWidth: 600,
      [theme.breakpoints.down('md')]: {
        width: '100%',
        marginBottom: theme.spacing(2),
      },
    },
    preview: {
      width: 240,
      maxWidth: 600,
      [theme.breakpoints.down('md')]: {
        width: '100%',
      },
    },
    appBar: {
      height,
      borderRadius: theme.shape.borderRadius,
    },
    toolbar: {
      position: 'relative',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'flex-start',
    },
    logo: {
      height,
      flex: '1 1 auto',
      padding: theme.spacing(1),
      display: 'block',
    },
    background: {
      height: 200,
      margin: theme.spacing(0, -2),
      boxShadow: theme.shadows[2],
    },
    avatar: {
      width: 128,
      height: 128,
      border: `1px solid ${theme.palette.divider}`,
    },
  };
});

export default Theming;
